import { Notification } from '@/models/Notification'
import { GlobalNotification } from '@/models/GlobalNotification'
import { Notification as NotificationResource, GlobalNotification as GlobalNotificationResource } from '@/resources/forge'

const store = {
  namespaced: true,
  state: {
    list: [],
    globalNotifications: [],
    combinedFeed: [],
    messages: [],
    activeTab: 'activity', // 'activity' | 'global' | 'combined'
    unreadCounts: {
      activity: 0,
      global: 0
    },
  },
  mutations: {
    /**
     * Sets the list of notifications in the store state.
     * @param {Array} list - The list of notifications.
     */
    setList(state, list) {
      state.list = list
    },
    /**
     * Deletes a notification from the list based on its ID.
     * @param {string} id - The ID of the notification to delete.
     */
    delete(state, id) {
      state.list = state.list.filter((f) => id != f.id)
    },
    /**
     * Adds a new notification to the list.
     * @param {Object} notification - The notification to add.
     */
    create(state, notification) {
      state.list = [...state.list, notification]
    },
    /**
     * Adds a new notification to the beginning of the list.
     * @param {Object} data - The data of the notification to add.
     */
    addNotification(state, data) {
      state.list = [new Notification(data), ...state.list]
    },
    /**
     * Updates an existing notification in the list.
     * @param {Object} params - The parameters to update the notification.
     */
    update(state, params) {
      const notification = state.list.find((n) => n.id == params.id)
      if (!notification) return

      Object.assign(notification, params)
    },
    /**
     * Sets the active tab for notifications.
     * @param {string} tab - The active tab ('activity' | 'global' | 'combined').
     */
    setActiveTab(state, tab) {
      state.activeTab = tab
    },
    /**
     * Sets the list of global notifications.
     * @param {Array} list - The list of global notifications.
     */
    setGlobalNotifications(state, list) {
      state.globalNotifications = list
    },
    /**
     * Sets the combined feed of notifications.
     * @param {Array} list - The combined feed of notifications.
     */
    setCombinedFeed(state, list) {
      state.combinedFeed = list
    },
    /**
     * Adds a new global notification to the list.
     * @param {Object} data - The data of the global notification to add.
     */
    addGlobalNotification(state, data) {
      const globalNotification = new GlobalNotification(data)
      state.globalNotifications = [globalNotification, ...state.globalNotifications]
    },
    /**
     * Updates unread counts for notifications.
     * @param {Object} counts - Object containing activity and global unread counts.
     */
    setUnreadCounts(state, counts) {
      state.unreadCounts = { ...state.unreadCounts, ...counts }
    },
    /**
     * Updates read status of a global notification.
     * @param {string} id - The ID of the global notification.
     */
    updateGlobalNotificationReadStatus(state, id) {
      const notification = state.globalNotifications.find((n) => n.id == id)
      if (notification) {
        notification.read = true
      }
      // Also update in combined feed if it exists
      const combinedNotification = state.combinedFeed.find((n) => n.id == id && n.type === 'global')
      if (combinedNotification && combinedNotification.data) {
        combinedNotification.data.read = true
      }
    },
    /**
     * Marks all global notifications as read.
     */
    markAllGlobalNotificationsAsRead(state) {
      state.globalNotifications.forEach(notification => {
        notification.read = true
      })
      // Also update in combined feed
      state.combinedFeed.forEach(item => {
        if (item.type === 'global' && item.data) {
          item.data.read = true
        }
      })
    },
    /**
     * Adds a message to the messages array for toast notifications.
     * @param {Object} message - The message to add.
     */
    addMessage(state, message) {
      state.messages = [...state.messages, message]
    },
    /**
     * Removes all messages from the messages array.
     */
    removeAllMessages(state) {
      state.messages = []
    },
  },
  actions: {
    /**
     * Loads notifications from the API and sets them in the store state.
     * @param {boolean} load_all - Whether to load all notifications or not.
     */
    async load({ commit }, load_all) {
      const res = await NotificationResource.get({ all: load_all })
      const notificationsArray = res.body.notifications
      commit(
        'setList',
        notificationsArray.map((n) => new Notification(n))
      )
      return res
    },
    /**
     * Updates the view state of a notification.
     * @param {string} id - The ID of the notification to update.
     */
    updateViewState({ commit, state }, id) {
      let notification = _.cloneDeep(state.list.find((n) => n.id == id))
      if (!notification) return
      notification.viewed = !notification.viewed
      commit('update', notification)
      NotificationResource.update(
        { id: notification.id },
        { notifications: { viewed: notification.viewed } }
      )
    },
    /**
     * Deletes a notification from the API and the store state.
     * @param {string} id - The ID of the notification to delete.
     */
    delete({ commit }, id) {
      NotificationResource.destroy({ id: id })
        .then(() => {
          commit('delete', id)
        })
        .catch((err) => {
          // Error deleting notification
        })
    },
    /**
     * Sets all notifications to viewed in the API and updates the store state.
     */
    async setAllToView({ commit }) {
      const res = await NotificationResource.setAllToView()
      const notificationsArray = res.body.notifications
      commit(
        'setList',
        notificationsArray.map((n) => new Notification(n))
      )
      return res
    },
    /**
     * Loads the combined feed from the API.
     */
    async loadCombinedFeed({ commit }) {
      try {
        const res = await NotificationResource.combined()

        
        // Handle different response formats
        let combinedFeed = res.body
        if (!Array.isArray(combinedFeed)) {
          // If the response is an object, try to extract the array
          if (combinedFeed && combinedFeed.notifications) {
            combinedFeed = combinedFeed.notifications
          } else if (combinedFeed && combinedFeed.data) {
            combinedFeed = combinedFeed.data
          } else {
            // If we can't find an array, create an empty one
            console.warn('Combined feed response is not an array:', combinedFeed)
            combinedFeed = []
          }
        }
        
        // Separate activity and global notifications
        const activityNotifications = []
        const globalNotifications = []
        
        if (Array.isArray(combinedFeed)) {
          combinedFeed.forEach(item => {
            if (item.type === 'global') {
              globalNotifications.push(new GlobalNotification(item))

            } else if (item.type === 'activity') {
              // Activity notifications have nested structure: data.notification
              const notificationData = item.data?.notification || item.data || item
              activityNotifications.push(new Notification(notificationData))

            } else {
              // Fallback for unknown types
              console.warn('Unknown notification type:', item.type, item)
              activityNotifications.push(new Notification(item.data || item))
            }
          })
        }
        
        commit('setList', activityNotifications)
        commit('setGlobalNotifications', globalNotifications)
        commit('setCombinedFeed', combinedFeed)
        
        // Calculate unread counts
        const activityUnread = activityNotifications.filter(n => !n.viewed).length
        const globalUnread = globalNotifications.filter(n => !n.read).length
        commit('setUnreadCounts', { activity: activityUnread, global: globalUnread })
        
        return res
      } catch (error) {
        console.error('Error loading combined feed:', error)
        // Fallback to loading notifications separately
        try {
          await this.dispatch('notifications/load', false)
          await this.dispatch('notifications/loadGlobalNotifications')
        } catch (fallbackError) {
          console.error('Fallback loading also failed:', fallbackError)
        }
        throw error
      }
    },
    /**
     * Loads global notifications only.
     */
    async loadGlobalNotifications({ commit }) {
      try {
        const res = await GlobalNotificationResource.get()

        
        // Handle different response formats
        let globalNotificationsData = res.body
        if (globalNotificationsData.global_notifications) {
          globalNotificationsData = globalNotificationsData.global_notifications
        } else if (globalNotificationsData.data) {
          globalNotificationsData = globalNotificationsData.data
        } else if (!Array.isArray(globalNotificationsData)) {
          console.warn('Global notifications response is not an array:', globalNotificationsData)
          globalNotificationsData = []
        }
        
        const notifications = Array.isArray(globalNotificationsData) 
          ? globalNotificationsData.map(n => new GlobalNotification(n))
          : []
          
        commit('setGlobalNotifications', notifications)
        
        const unreadCount = notifications.filter(n => !n.read).length
        commit('setUnreadCounts', { global: unreadCount })
        
        return res
      } catch (error) {
        console.error('Error loading global notifications:', error)
        // Set empty array on error to prevent UI issues
        commit('setGlobalNotifications', [])
        commit('setUnreadCounts', { global: 0 })
        throw error
      }
    },
    /**
     * Marks a global notification as read.
     * @param {string} id - The ID of the global notification.
     */
    async markGlobalNotificationAsRead({ commit }, id) {
      try {
        const response = await GlobalNotificationResource.markAsRead({ id }, {})
        commit('updateGlobalNotificationReadStatus', id)
        
        // Update unread count
        const globalNotifications = this.state.notifications.globalNotifications
        const unreadCount = globalNotifications.filter(n => !n.read).length
        commit('setUnreadCounts', { global: unreadCount })
      } catch (error) {
        console.error('Error marking global notification as read:', error)
        
        // Check if it's a 404 (endpoint not implemented) or CORS issue
        if (error.status === 404 || error.status === 0) {
          console.warn('Backend endpoint not yet implemented. Updating UI only for now.')
          // Update UI optimistically even if backend call fails
          commit('updateGlobalNotificationReadStatus', id)
          
          // Update unread count
          const globalNotifications = this.state.notifications.globalNotifications
          const unreadCount = globalNotifications.filter(n => !n.read).length
          commit('setUnreadCounts', { global: unreadCount })
        } else {
          throw error
        }
      }
    },
    /**
     * Marks all global notifications as read.
     */
    async markAllGlobalNotificationsAsRead({ commit }) {
      try {
        await GlobalNotificationResource.markAllAsRead()
        commit('markAllGlobalNotificationsAsRead')
        commit('setUnreadCounts', { global: 0 })
      } catch (error) {
        console.error('Error marking all global notifications as read:', error)
        
        // Check if it's a 404 (endpoint not implemented) or CORS issue
        if (error.status === 404 || error.status === 0) {
          console.warn('Backend endpoint not yet implemented. Updating UI only for now.')
          // Update UI optimistically even if backend call fails
          commit('markAllGlobalNotificationsAsRead')
          commit('setUnreadCounts', { global: 0 })
        } else {
          throw error
        }
      }
    },
    /**
     * Gets unread count for global notifications.
     */
    async getGlobalUnreadCount({ commit }) {
      try {
        const res = await GlobalNotificationResource.unreadCount()
        const count = res.body.unread_count || 0
        commit('setUnreadCounts', { global: count })
        return count
      } catch (error) {
        console.error('Error getting global unread count:', error)
        return 0
      }
    },
    /**
     * Sets the active tab.
     * @param {string} tab - The tab to set as active.
     */
    setActiveTab({ commit }, tab) {
      commit('setActiveTab', tab)
    },
  },
}

export default store
